/**
 * What the assistant is not allowed to see or fill in on the screen behind it.
 *
 * The form bridge is a second door to the same data `ChatReadAllowlist` guards on the API side, and
 * it would be a wider one if it were left open: that file decides which *columns* come back from a
 * lookup, while this one decides what an open form publishes, values and all. A screen bridged
 * without thought sends every input's contents to the model with each message.
 *
 * So there are two boundaries here, and one file holding both, for the same reason the read
 * allowlist is one file: **a thing nobody named cannot get out.**
 */

/**
 * Resources the assistant is locked out of entirely (user, 2026-09-09).
 *
 * Not "fields to withhold on these screens" - the whole screen. The assistant sees no form open at
 * all, which is exactly the state of a screen that never opted in, so it needs no special wording
 * anywhere and behaves as it did before any of this existed.
 *
 * These are the names `useResourceContext()` returns, taken from the `<Resource name=...>`
 * registrations in `AdminApp.tsx`, not the component names.
 *
 * - `operators`    holds `token` and `responseToken`
 * - `admin_users`  holds passwords, and the roles that decide what an admin may do
 * - `settings`     `ChatReadAllowlist` excludes `Setting` outright because its `value` column can
 *                  hold anything, and letting a form fill it in would walk round that decision
 * - `prompts`, `model_schemas`, `tools` are one decision: **the assistant does not get to propose
 *   changes to how it and its siblings behave**
 *
 * Enforced here rather than by leaving `<ChatFieldBridge />` out of those twelve files. Omitting
 * the line is the belt and this is the braces: a boundary that holds only while nobody adds a line
 * is not a boundary.
 */
export const LOCKED_RESOURCES: ReadonlySet<string> = new Set([
    'operators',
    'admin_users',
    'settings',
    'prompts',
    'model_schemas',
    'tools',
]);

/**
 * Field names never published, on any screen that *is* bridged.
 *
 * The second line rather than the only one, the way `ChatRead` keeps its own `instanceof DaiveUser`
 * check even though the firewall already refused the request. It is what covers a credential
 * sitting on a screen that is otherwise perfectly reasonable to bridge - `AdminWebsiteEdit` holds a
 * `verificationToken` next to a URL and a name - and what covers a screen that gains one later
 * without anybody noticing.
 *
 * Matched on the name rather than kept as a list of screens, because a list of screens is the thing
 * that rots. Substring and case-insensitive, so `verificationTokenExpiration`, `new_password` and
 * `stripeId` are all caught without being written down.
 */
const WITHHELD_NAME_PARTS: readonly string[] = [
    'password',
    'token',
    'secret',
    'apikey',
    'api_key',
    'encryptionkey',
    'encryption_key',
    'stripeid',
    'stripe_id',
    'hash',
];

/**
 * Whether the assistant is locked out of this screen entirely.
 *
 * @param resource the resource name, from `useResourceContext()`
 */
export function isLockedResource(resource: string | undefined): boolean {
    return resource === undefined || LOCKED_RESOURCES.has(resource);
}

/**
 * Whether this field is withheld wherever it appears.
 *
 * @param field the input's `source`, as the form registered it
 */
export function isWithheldField(field: string): boolean {
    const name: string = field.toLowerCase();

    return WITHHELD_NAME_PARTS.some((part: string): boolean => name.includes(part));
}

/**
 * The fields a form may publish, out of the ones it has mounted.
 *
 * Filtering the *list* is what closes both halves at once. `describeScreen` only reads values for
 * fields in it, so a withheld field's contents never leave the browser; and `propose_field_value`
 * refuses any field the screen did not publish, so the assistant cannot offer a value for one
 * either. There is no third thing to remember.
 *
 * @param fields every input the form has mounted
 */
export function publishableFields(fields: string[]): string[] {
    return fields.filter((field: string): boolean => !isWithheldField(field));
}
